// ============================================================
// copyText - clipboard write that survives a non-secure origin.
//
// `navigator.clipboard` only exists in a SECURE CONTEXT: https, or
// localhost. This app is routinely opened over plain http on the
// tailnet (see ./dev remote|sandbox), where the property is undefined
// and every copy button silently does nothing - it looks like the
// feature is disabled.
//
// The execCommand fallback is deprecated but is the only thing that
// works on a non-secure origin, and it is what every other app does.
// ============================================================

/** The deprecated fallback that still works on a non-secure origin: select a hidden textarea and copy. */
function execCommandCopy(text: string): boolean {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    // Off-screen but focusable; `readOnly` stops the mobile keyboard.
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default async function copyText(text: string): Promise<boolean> {
  if (!text) return false;
  // THE E2E RUNNER CANNOT READ A CLIPBOARD, so when main.jsx has armed the
  // emulator build's hooks the last text copied is also left on the window
  // for a spec to read (v8.362.0). Never armed in a production build.
  const w = window as unknown as { __e2eCaptureCopies?: boolean; __e2eLastCopiedText?: string };
  if (w.__e2eCaptureCopies) w.__e2eLastCopiedText = text;

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through - permission denied, or a non-secure origin */
    }
  }

  return execCommandCopy(text);
}
